/** @jsxImportSource @emotion/react */
import React, { useRef } from "react";
import { Amount } from "../../types/amount";
import { NetworkFeeLabel } from "./NetworkFeeLabel";
import { SlippageRow } from "./SlippageRow";
import { BeforeNetworkFeeRow } from "./BeforeNetworkFeeRow";
import { NetworkFeeRow } from "./NetworkFeeRow";
import { RatesRow } from "./RatesRow";
import { getOrderType, OrderType } from "../../utils/orderTypeUtils";
import { GlobalStore } from "../../store/global/GlobalStore";
import { WillBeConvertedRow } from "./WillBeConvertedRow";
import { TradeDetailsRow } from "./elements/TradeDetailsRow";
import { AccordionDownIcon } from "./elements/AccordionDownIcon";
import { Spacer } from "../general/Spacer/Spacer";
import AnimatedDropdownContent from "../AnimatedDropdown/AnimatedDropdown";
import { useDisplayOnClick } from "../../hooks/useDisplayOnClick";
import { TradeDetailsButton } from "./elements/TradeDetailsButton";
import { FlexBetweenContainer } from "../../containers/FlexBetweenContainer";
import { keyframes } from "@emotion/react";
import { Text } from "../general/Text";

export interface DetailsSectionProps {
  networkFee: Amount;
  visible: boolean;
}

/**
 * For different Order Types we order the components in a different way.
 * For 121/12M Orders: Network fees, Will Be Converted,Rates, Slippage
 * For M21 Orders: Rates,Before Network Fees,Eth Fees, Slippage
 */
const LAYOUT: Record<OrderType, Array<React.ReactElement>> = {
  [OrderType.OneToOne]: [<NetworkFeeRow />, <WillBeConvertedRow />, <RatesRow />, <SlippageRow />],
  [OrderType.OneToMany]: [<NetworkFeeRow />, <WillBeConvertedRow />, <RatesRow />, <SlippageRow />],
  [OrderType.ManyToOne]: [
    <RatesRow />,
    <BeforeNetworkFeeRow />,
    <NetworkFeeRow />,
    <SlippageRow />,
  ],
};
export const TradeDetailsSection: React.FC<DetailsSectionProps> = props => {
  const { onClick, isDisplayed, animationState } = useDisplayOnClick({ duration: 250 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const orderType = GlobalStore.useState(s => {
    return getOrderType(s);
  });

  if (!props.visible) {
    return null;
  }
  return (
    <div data-testid={"details-section-container"}>
      <FlexBetweenContainer>
        <TradeDetailsButton
          ref={buttonRef}
          type="button"
          onClick={() => onClick()}
          data-testid="trade-details-btn"
        >
          <Text textStyle="Title" size="XS">
            Trade Details
          </Text>
          <Spacer width="2px" />
          <AccordionDownIcon width={16} height={16} isAccordionOpen={isDisplayed} />
        </TradeDetailsButton>
        <NetworkFeeLabel networkFee={props.networkFee} />
      </FlexBetweenContainer>
      {isDisplayed && <Spacer height="8px" />}
      {isDisplayed && (
        <AnimatedDropdownContent
          animationState={animationState}
          duration={250}
          openingAnimation={openingTradeDetailsAnimation}
          closingAnimation={closingTradeDetailsAnimation}
          data-testid="details-section-content"
        >
          <TradeDetailsRow className="accordion-body">
            {orderType &&
              LAYOUT[orderType as unknown as OrderType].map((element, index) => {
                return <div key={index}>{element}</div>;
              })}
          </TradeDetailsRow>
        </AnimatedDropdownContent>
      )}
    </div>
  );
};

// Animation
export const openingTradeDetailsAnimation = keyframes`
  0% {
    max-height: 0;
    opacity: 0;
  }
  100% {
    max-height: 450px;
    opacity: 1;
  }
`;

export const closingTradeDetailsAnimation = keyframes`
  0% {
    max-height: 450px;
    opacity: 1;
  }
  100% {
    max-height: 0px;
    opacity: 0;
  }
`;
